import { FC, useState, memo } from 'react';
import { AspectRatio, Box, Image, Link, SimpleGrid } from '@chakra-ui/react';
import isEqual from 'lodash/fp/isEqual';
import { DownloadShareableModal } from './DownloadShareableModal';
import { ShareableImage, ShareableSection } from './ShareablesContent';

/**
 * @prop ShareableSection.title string
 * @prop ShareableSection.images
 */

export const ShareablesSection: FC<ShareableSection> = memo(
  ({ images, moreSizesUrl }: ShareableSection): JSX.Element | null => {
    const [isOpen, setIsOpen] = useState(false);
    const initialImage = images.find(
      (image): image is ShareableImage =>
        !!image.previewUrl &&
        !!image.horizontalUrl &&
        !!image.squareUrl &&
        !!image.verticalUrl
    );
    const [selectedImage, setSelectedImage] = useState(initialImage);

    const onSelectImage = (index: number): void => {
      const image = images[index];
      if (!image.horizontalUrl || !image.squareUrl || !image.verticalUrl) {
        return;
      }

      setIsOpen(true);
      setSelectedImage(image);
    };

    if (!images.length) return null;

    const getDownloadName = (url: string): string =>
      url.split('/').pop() || 'shareable';

    return (
      <Box mt={8}>
        {selectedImage && (
          <DownloadShareableModal
            isOpen={isOpen}
            previewUrl={selectedImage.previewUrl}
            horizontalUrl={selectedImage.horizontalUrl as string}
            squareUrl={selectedImage.squareUrl as string}
            verticalUrl={selectedImage.verticalUrl as string}
            title={selectedImage.title}
            altText={selectedImage.altText}
            onClosed={() => setIsOpen(false)}
          />
        )}

        <SimpleGrid columns={2} spacing={2}>
          {images.map((image, index) => {
            if (
              !image.horizontalUrl ||
              !image.squareUrl ||
              !image.verticalUrl
            ) {
              return (
                <AspectRatio key={index} ratio={1}>
                  <Box bg="gray.50" borderRadius="4px" overflow="hidden" p={1}>
                    <a
                      href={image.downloadUrl || image.previewUrl}
                      download={getDownloadName(
                        image.downloadUrl || image.previewUrl
                      )}
                      style={{
                        display: 'block',
                        height: '100%',
                        textDecoration: 'none',
                        width: '100%',
                      }}
                    >
                      <Image
                        alt={image.altText}
                        borderRadius="4px"
                        cursor="pointer"
                        h="100%"
                        objectFit="contain"
                        src={image.previewUrl}
                        w="100%"
                      />
                    </a>
                  </Box>
                </AspectRatio>
              );
            }

            return (
              <AspectRatio key={index} ratio={1}>
                <Box bg="gray.50" borderRadius="4px" overflow="hidden" p={1}>
                  <Image
                    alt={image.altText}
                    borderRadius="4px"
                    cursor="pointer"
                    h="100%"
                    objectFit="contain"
                    onClick={() => onSelectImage(index)}
                    src={image.previewUrl}
                    w="100%"
                  />
                </Box>
              </AspectRatio>
            );
          })}
        </SimpleGrid>

        {moreSizesUrl && (
          <Link
            color="brand.additional.link"
            display="inline-block"
            mt={4}
            href={moreSizesUrl}
          >
            See more sizes
          </Link>
        )}
      </Box>
    );
  },
  isEqual
);
